import type { JWTPayload } from 'jose';

const ACTIVE_CLIENT_COOKIE = 'ciam_active_client';

export interface ActiveClient {
  clientId: string;
  provider: 'auth0' | 'fusionauth' | '';
}

interface ActiveClientCookie {
  activeIdp?: string;
  clients?: Array<{ id?: string; idp?: string }>;
}

// Reads the `ciam_active_client` cookie and returns the client matching the active IDP, or null.
export function getActiveClient(): ActiveClient | null {
  const raw = getCookieByName(ACTIVE_CLIENT_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as ActiveClientCookie;
    const active = parsed.clients?.find((c) => c.idp === parsed.activeIdp);
    if (!active?.id) return null;
    const provider = active.idp === 'auth0' || active.idp === 'fusionauth' ? active.idp : '';
    return { clientId: active.id, provider };
  } catch {
    return null;
  }
}

// Prefix from the active-client cookie's provider; falls back to trying both when absent.
export function getCookie(cookieName: string): string | null {
  const provider = getActiveClient()?.provider;
  if (provider === 'auth0') return getCookieByName(`auth0_${cookieName}`);
  if (provider === 'fusionauth') return getCookieByName(`ciam_${cookieName}`);
  return getCookieByName(`ciam_${cookieName}`) ?? getCookieByName(`auth0_${cookieName}`);
}

function getCookieByName(fullCookieName: string): string | null {
  const cookies = document.cookie.split(';');
  for (const c of cookies) {
    const [name, ...value] = c.split('=');
    if (name.trim() === fullCookieName) {
      return value.join('=');
    }
  }
  return null;
}

export async function extractToken<T extends JWTPayload>(cookieName: string): Promise<T | null> {
  const cookie = getCookie(cookieName);

  if (!cookie) {
    return null;
  }

  const { decodeJwt } = await import('../vendor/jose');

  return decodeJwt(cookie) as T;
}
